/**
 * What stands where a chart would be when the renderer cannot be licensed.
 *
 * **The whole point is that this is ours.** SciChart's own two failure states —
 * a licence notice drawn *instead of* the chart, and a "Powered by SciChart"
 * watermark drawn *over* it — are both unacceptable in front of a user
 * (requirement, 2026-09-03). Neither can be intercepted after the fact, since
 * both are drawn by `updateLicenseDisplay` per surface at construction, so
 * `chartsLicensed()` withholds the surface entirely and this takes its place.
 *
 * ## The copy says nothing a user could act on, because there is nothing
 *
 * A licence is a fact about our deployment. Naming the vendor, the key or the
 * word "licence" invites a person to go looking for a setting that does not
 * exist on their side, and tells anyone reading over their shoulder more about
 * our build than they need. So: it is unavailable, it is known, and the rest is
 * in the console for whoever is on call.
 *
 * `why` is deliberately **not** rendered. It is carried so that a caller can log
 * it and so this component has one obvious place to grow a variant if the
 * distinction ever matters to a reader — today it does not, because all four
 * mean the same thing to the person looking at the screen.
 *
 * ## Scoped to the chart, never the app
 *
 * The screener, the journal, fundamentals and the symbol search need no WebGL
 * and no licence. An app that refuses to start over a chart licence is a worse
 * outage than the watermark this replaces, so this renders inside one section
 * and everything else carries on.
 */

import type { ChartsVerdict } from "../lib/scichart-bootstrap";

export interface ChartsUnavailableProps {
  /** Why the gate withheld the surface. Not shown; see the note above. */
  why: Extract<ChartsVerdict, { charts: "blocked" }>["why"];
}

export function ChartsUnavailable({ why }: ChartsUnavailableProps): JSX.Element {
  return (
    <div
      // `data-*` rather than visible text: the reason belongs to whoever is
      // debugging, and the DOM is where they will already be looking.
      data-charts-blocked={why}
      role="status"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.4rem",
        padding: "1rem",
        // The chart's own ground, so the section does not read as missing —
        // it reads as present and empty, which is what it is.
        background: "var(--qf-surface)",
        color: "var(--qf-text-secondary)",
        textAlign: "center",
        // A chart pane is a fixed cell; this must not be the thing that makes
        // the grid scroll.
        overflow: "hidden",
      }}
    >
      <div style={{ fontSize: "0.95rem", color: "var(--qf-text)" }}>
        Charts are temporarily unavailable
      </div>
      <div style={{ fontSize: "0.8rem", maxWidth: "32ch", lineHeight: 1.4 }}>
        Everything else still works. We know about this and are on it.
      </div>
    </div>
  );
}
